import React, { useState, useEffect, useRef } from 'react';
import Confetti from '../ui/Confetti';
import { playCorrectSound, playWrongSound, playSuccessSound, playGameOverSound, playKeyPressSound } from '../../utils/audio';
import RpgSprite from './RpgSprite';
import { User } from '../../types';
import { api } from '../../utils/api';

interface TypingRpgGameProps {
    user?: User;
}

type Scene = {
    description: string;
    options: { [command: string]: string };
    monster?: { name: string; hp: number };
    image?: string;
    nextLevel?: boolean;
    victory?: boolean;
};

export type Level = {
    title: string;
    allowedKeys: string;
    startScene: string;
    scenes: { [key: string]: Scene };
};

const ALL_GAME_KEYS = "abcdefghijklmnopqrstuvwxyz,.";

const initialStory: Level[] = [
    // ACT 1: The Beginning of the Silence
    {
        title: "Level 1: The Fading Village",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "Your village is quiet. Words fade from signs, stories are forgotten. The Elder tells you of the 'Curse of Silence'. You must seek the King. Type 'ask' to learn more.", image: "mentor", options: { "ask": "journey" } },
            journey: { description: "You accept the quest. Your journey to the King's castle begins now. The path is oddly quiet. Type 'go' to travel.", image: "player", options: { "go": "level_end" } },
            level_end: { description: "You arrive at the castle gates, determined to find a cure. Type 'enter' to seek an audience with the King.", image: "player", nextLevel: true, options: { "enter": "" } }
        }
    },
    {
        title: "Level 2: The King's Plea",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "The King is distraught. 'The Silence affects us all. A foul Slime in the cellar seems to be a source of this foul magic. Please, investigate.'", image: "mentor", options: { "accept": "approach" } },
            approach: { description: "You find the Slime in the castle cellar. It jiggles defiantly, radiating an aura of forgetfulness.", image: "slime", options: { "a sad slime": "slime_fight" } },
            slime_fight: { description: "The Sad Slime wobbles, ready to attack! Type 'slash' or 'fall' to fight!", image: "slime", monster: { name: "Sad Slime", hp: 10 }, options: { "slash": "slime_hit", "fall": "slime_hit" } },
            slime_hit: { description: "You attack the slime!", image: "slime", monster: { name: "Sad Slime", hp: 10 }, options: { "slash": "slime_hit", "fall": "slime_hit" } },
            slime_victory: { description: "The slime dissolves. The curse in this room fades slightly. 'This is a good sign,' the King says. 'Take this map to the Word Shrine in the forest.'", image: "player", options: { "a map": "level_end" } },
            level_end: { description: "The map shows a path into the Whisperwood. Type 'all' to continue.", image: "player", nextLevel: true, options: { "all": "" } }
        }
    },
    {
        title: "Level 3: Whisperwood Path",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "The Upper Path into Whisperwood is eerie. A Gremlin is tearing pages from a book, eating the words! You must stop it.", image: "gremlin", options: { "stop": "gremlin_fight", "quiet": "gremlin_fight" } },
            gremlin_fight: { description: "The Gremlin screeches, its mind addled by the curse! Type 'power' or 'strike' to fight back!", image: "gremlin", monster: { name: "Grumpy Gremlin", hp: 15 }, options: { "power": "gremlin_hit", "strike": "gremlin_hit" } },
            gremlin_hit: { description: "You clash with the Gremlin!", image: "gremlin", monster: { name: "Grumpy Gremlin", hp: 15 }, options: { "power": "gremlin_hit", "strike": "gremlin_hit" } },
            gremlin_victory: { description: "The Gremlin is subdued. It whimpers and points to a door with a riddle: 'I have cities, no houses. Forests, no trees. Water, no fish. What am I?'", image: "player", options: { "a map": "level_end" } },
            level_end: { description: "The door opens! The path forward is clear. Type 'prepare' to venture deeper.", image: "player", nextLevel: true, options: { "prepare": "" } }
        }
    },
    {
        title: "Level 4: The Foggy Maze",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "The fog of the curse is thick here, confusing your senses. You hear leathery wings. Three paths lie ahead.", image: "player", options: { "left": "game_over_lost", "right": "bat_path", "straight": "game_over_lost" } },
            game_over_lost: { description: "You wander aimlessly in the thick, disorienting fog. The curse drains your will, and you are never seen again. Game Over.", image: "player", options: {} },
            bat_path: { description: "You hear a screech! A Shadow Bat, a creature born of forgotten sounds, swoops from the fog!", image: "bat", options: { "fight": "bat_fight" } },
            bat_fight: { description: "The bat flies in circles! Type 'thrust' or 'slash' to hit it!", image: "bat", monster: { name: "Shadow Bat", hp: 20 }, options: { "thrust": "bat_hit", "slash": "bat_hit" } },
            bat_hit: { description: "You fight the circling bat!", image: "bat", monster: { name: "Shadow Bat", hp: 20 }, options: { "thrust": "bat_hit", "slash": "bat_hit" } },
            bat_victory: { description: "The Shadow Bat falls. The fog thins, revealing a passage leading down to a flooded cavern.", image: "player", options: { "go": "level_end" } },
            level_end: { description: "You have found the way through the maze. Type 'descend' to continue.", image: "player", nextLevel: true, options: { "descend": "" } }
        }
    },
    {
        title: "Level 5: The Flooded Cavern",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "Water drips from the ceiling. A vast underground lake is before you. You see a boat.", image: "player", options: { "boat": "take_boat", "swim": "game_over_drown" } },
            game_over_drown: { description: "You plunge into the freezing water. The cold seeps into your bones instantly, and the dark water pulls you under. Game Over.", image: "player", options: {} },
            take_boat: { description: "You cross the lake. On an island, a giant Cave Crab guards a treasure chest!", image: "crab", options: { "open": "crab_fight", "examine": "crab_fight" } },
            crab_fight: { description: "The crab snaps its massive claws! Type 'smack' or 'bash' to attack!", image: "crab", monster: { name: "Cave Crab", hp: 25 }, options: { "smack": "crab_hit", "bash": "crab_hit" } },
            crab_hit: { description: "You battle the giant crab!", image: "crab", monster: { name: "Cave Crab", hp: 25 }, options: { "smack": "crab_hit", "bash": "crab_hit" } },
            crab_victory: { description: "The crab scuttles away. You open the chest and find a Dwarven key and a shield. A locked Dwarven door is nearby.", image: "shield", options: { "open": "level_end", "use key": "level_end" } },
            level_end: { description: "The Dwarven door groans open, revealing a vault. Type 'enter' to proceed.", image: "player", nextLevel: true, options: { "enter": "" } }
        }
    },
    {
        title: "Level 6: The Dwarven Gate",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "Before you is a great stone door with a Dwarven inscription. 'Speak the password to enter.' A carving shows a bed, a key, and a clock.", image: "shield", options: { "time": "game_over_trap", "sleep": "game_over_trap", "unlock": "game_over_trap", "keyboard": "correct_riddle" } },
            game_over_trap: { description: "An incorrect word triggers a trap! Darts shoot from the walls, and your quest ends here. Game Over.", image: "player", options: {} },
            correct_riddle: { description: "The door rumbles open! 'I have keys, but open no doors. I have space, but no room. You are using me now.' The password was 'keyboard'!", image: "player", options: { "onward": "level_end" } },
            level_end: { description: "You step into the dark mines. The air is stale. Type 'delve' to go deeper.", image: "player", nextLevel: true, options: { "delve": "" } }
        }
    },
    {
        title: "Level 7: The Chittering Deeps",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "The mine tunnels are covered in thick webs. You hear a skittering sound nearby. A giant Spider descends from the ceiling!", image: "spider", options: { "charge": "spider_fight" } },
            spider_fight: { description: "The spider hisses! Type 'venom' or 'strike' to attack its legs!", image: "spider", monster: { name: "Giant Spider", hp: 30 }, options: { "venom": "spider_hit", "strike": "spider_hit" } },
            spider_hit: { description: "You fight the giant spider!", image: "spider", monster: { name: "Giant Spider", hp: 30 }, options: { "venom": "spider_hit", "strike": "spider_hit" } },
            spider_victory: { description: "The spider collapses. You cut through the webs and find a dusty journal. It speaks of an old crypt where the Word Shrine may be.", image: "player", options: { "read": "level_end" } },
            level_end: { description: "The journal guides you to a hidden passage. Type 'press' to open it.", image: "player", nextLevel: true, options: { "press": "" } }
        }
    },
    {
        title: "Level 8: The Undead Crypt",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "A chill runs down your spine. A Skeleton Warrior, animated by the Silence, rises from a coffin!", image: "skeleton", options: { "defend": "skeleton_fight" } },
            skeleton_fight: { description: "The skeleton raises a rusty sword! Type 'bone' or 'rattle' to shatter its defenses!", image: "skeleton", monster: { name: "Skeleton Warrior", hp: 35 }, options: { "bone": "skeleton_hit", "rattle": "skeleton_hit" } },
            skeleton_hit: { description: "You fight the skeleton warrior!", image: "skeleton", monster: { name: "Skeleton Warrior", hp: 35 }, options: { "bone": "skeleton_hit", "rattle": "skeleton_hit" } },
            skeleton_victory: { description: "The skeleton crumbles into a pile of bones. The dark magic fades, revealing a spectral light ahead.", image: "player", options: { "follow": "level_end" } },
            level_end: { description: "The light leads you deeper into the crypt. Type 'enter' to follow.", image: "player", nextLevel: true, options: { "enter": "" } }
        }
    },
    {
        title: "Level 9: The Ghostly Halls",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "You enter a hall where ghostly figures drift. An Ancient Ghost, a remnant of a forgotten scribe, blocks your path.", image: "ghost", options: { "confront": "ghost_fight" } },
            ghost_fight: { description: "The ghost wails, a sound that chills your soul! Type 'spirit' or 'light' to banish it!", image: "ghost", monster: { name: "Ancient Ghost", hp: 40 }, options: { "spirit": "ghost_hit", "light": "ghost_hit" } },
            ghost_hit: { description: "You fight the ancient ghost!", image: "ghost", monster: { name: "Ancient Ghost", hp: 40 }, options: { "spirit": "ghost_hit", "light": "ghost_hit" } },
            ghost_victory: { description: "The ghost fades away, leaving behind an ethereal cloak. It allows you to pass through the next door unnoticed.", image: "player", options: { "wear": "level_end" } },
            level_end: { description: "You are now cloaked in shadow. Type 'sneak' to proceed.", image: "player", nextLevel: true, options: { "sneak": "" } }
        }
    },
    {
        title: "Level 10: The Goblin Warren",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "You sneak into a noisy warren. A Goblin Chief, larger than the others, sits on a makeshift throne of forgotten books.", image: "goblin", options: { "ambush": "goblin_fight" } },
            goblin_fight: { description: "The Goblin Chief spots you and snarls! Type 'quick' or 'attack' to gain the upper hand!", image: "goblin", monster: { name: "Goblin Chief", hp: 45 }, options: { "quick": "goblin_hit", "attack": "goblin_hit" } },
            goblin_hit: { description: "You fight the goblin chief!", image: "goblin", monster: { name: "Goblin Chief", hp: 45 }, options: { "quick": "goblin_hit", "attack": "goblin_hit" } },
            goblin_victory: { description: "The chief is defeated. You find a crude map leading to an Orc outpost, near the Word Shrine.", image: "player", options: { "take": "level_end" } },
            level_end: { description: "You follow the map out of the warren. Type 'march' to continue.", image: "player", nextLevel: true, options: { "march": "" } }
        }
    },
    {
        title: "Level 11: The Orc Outpost",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "The map leads to a crude fortress. An Orc Brute, armed with a massive club, guards the entrance.", image: "orc", options: { "challenge": "orc_fight" } },
            orc_fight: { description: "The Orc Brute roars and charges! Type 'smash' or 'power' to meet its strength!", image: "orc", monster: { name: "Orc Brute", hp: 50 }, options: { "smash": "orc_hit", "power": "orc_hit" } },
            orc_hit: { description: "You fight the orc brute!", image: "orc", monster: { name: "Orc Brute", hp: 50 }, options: { "smash": "orc_hit", "power": "orc_hit" } },
            orc_victory: { description: "The Orc Brute falls. The fortress gate is open, and within, you see the glowing Word Shrine.", image: "player", options: { "enter": "level_end" } },
            level_end: { description: "You've reached the shrine. Type 'awaken' to restore its power.", image: "player", nextLevel: true, options: { "awaken": "" } }
        }
    },
    {
        title: "Level 12: First Word Shrine",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "You place your hand on the cold stone. To awaken it, you must prove your mastery of words. A Golem of Living Letters forms before you!", image: "golem", options: { "begin": "golem_fight" } },
            golem_fight: { description: "The Golem attacks! It is made of pure language. Type 'focus' and 'clarity' to strike at its core!", image: "golem", monster: { name: "Alphabet Golem", hp: 55 }, options: { "focus": "golem_hit", "clarity": "golem_hit" } },
            golem_hit: { description: "You strike the Golem with powerful words!", image: "golem", monster: { name: "Alphabet Golem", hp: 55 }, options: { "focus": "golem_hit", "clarity": "golem_hit" } },
            golem_victory: { description: "The Golem crumbles. The Shrine glows, pushing back the Silence in the region. Your village is safe for now, but the curse remains.", image: "player", options: { "rest": "level_end" } },
            level_end: { description: "A vision shows you the next shrine, deep within a Corrupted Forest. Type 'venture' to continue your quest.", image: "player", nextLevel: true, options: { "venture": "" } }
        }
    },
    // ACT 2: The Corrupted Forest & Sunken City
    {
        title: "Level 13: Edge of Corruption",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "The forest ahead is dark and twisted. The trees groan. The Silence is strong here, twisting nature itself. A path leads into the gloom.", image: "player", options: { "forward": "level_end" } },
            level_end: { description: "You steel yourself and step into the corrupted woods. Type 'explore' to search for the shrine.", image: "player", nextLevel: true, options: { "explore": "" } }
        }
    },
    {
        title: "Level 14: Twisted Roots",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "The path is blocked by thick, thorny roots. As you approach, the roots twist into a monstrous shape. A Twisted Treant rises from the ground!", image: "treant", options: { "fight": "treant_fight" } },
            treant_fight: { description: "The Treant swings its branch-like arms! Type 'lumber' and 'timber' to chop it down!", image: "treant", monster: { name: "Twisted Treant", hp: 60 }, options: { "lumber": "treant_hit", "timber": "treant_hit" } },
            treant_hit: { description: "You hack away at the corrupted wood!", image: "treant", monster: { name: "Twisted Treant", hp: 60 }, options: { "lumber": "treant_hit", "timber": "treant_hit" } },
            treant_victory: { description: "The Treant splinters and falls, clearing the path. You find a worn sign pointing towards a 'Fen'.", image: "player", options: { "follow": "level_end" } },
            level_end: { description: "You follow the sign towards the boggy fen. Type 'trudge' to press on.", image: "player", nextLevel: true, options: { "trudge": "" } }
        }
    },
    {
        title: "Level 15: The Gloom Fen",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "You enter a murky swamp. Strange lights drift between the trees. These are Gloom Wisps, spirits of lost words.", image: "wisp", options: { "approach": "wisp_fight" } },
            wisp_fight: { description: "A Wisp floats towards you, its light draining your thoughts! Type 'memory' and 'recall' to fight its influence!", image: "wisp", monster: { name: "Gloom Wisp", hp: 50 }, options: { "memory": "wisp_hit", "recall": "wisp_hit" } },
            wisp_hit: { description: "You focus your mind against the Wisp!", image: "wisp", monster: { name: "Gloom Wisp", hp: 50 }, options: { "memory": "wisp_hit", "recall": "wisp_hit" } },
            wisp_victory: { description: "The Wisp brightens and fades away, leaving a clear memory of a path forward.", image: "player", options: { "path": "level_end" } },
            level_end: { description: "The memory guides you to the edge of a great lake. Type 'gaze' to look upon it.", image: "player", nextLevel: true, options: { "gaze": "" } }
        }
    },
    {
        title: "Level 16: Lake of Lost Syllables",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "In the center of the lake, you see the spire of a sunken tower. The next Word Shrine must be there. A rickety raft is nearby.", image: "player", options: { "raft": "level_end" } },
            level_end: { description: "You push the raft into the dark water. Type 'paddle' to cross the lake.", image: "player", nextLevel: true, options: { "paddle": "" } }
        }
    },
    {
        title: "Level 17: Murky Depths",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "As you paddle, a creature emerges from the murky water. A Mire Lurker, its shell covered in forgotten runes, blocks your way.", image: "mire_lurker", options: { "defend": "mire_fight" } },
            mire_fight: { description: "The Mire Lurker attacks your raft! Type 'repel' and 'divert' to protect yourself!", image: "mire_lurker", monster: { name: "Mire Lurker", hp: 65 }, options: { "repel": "mire_hit", "divert": "mire_hit" } },
            mire_hit: { description: "You fend off the swamp beast!", image: "mire_lurker", monster: { name: "Mire Lurker", hp: 65 }, options: { "repel": "mire_hit", "divert": "mire_hit" } },
            mire_victory: { description: "The Mire Lurker sinks back into the depths. You have reached the entrance to the sunken tower.", image: "player", options: { "enter": "level_end" } },
            level_end: { description: "You tie off the raft and enter the ancient structure. Type 'delve' to explore.", image: "player", nextLevel: true, options: { "delve": "" } }
        }
    },
    {
        title: "Level 18: Sunken City of Scribes",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "This was once a great library. Waterlogged books drift like ghosts. The central chamber is ahead, but the way is blocked.", image: "player", options: { "search": "level_end" } },
            level_end: { description: "You must find a way to open the door to the shrine's chamber. Type 'investigate' to look for clues.", image: "player", nextLevel: true, options: { "investigate": "" } }
        }
    },
    {
        title: "Level 19: The Silent Librarian",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "A ghostly figure appears. The Silent Librarian. It points to a series of shelves. You must complete its challenge to pass.", image: "ghost", options: { "accept": "level_end" } },
            level_end: { description: "The ghost wants you to prove your worth. Type 'knowledge' to begin the trial.", image: "player", nextLevel: true, options: { "knowledge": "" } }
        }
    },
    {
        title: "Level 20: Trial of Tomes",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "To pass, you must type the ancient words of power as they appear on the spectral tomes.", image: "player", options: { "the pen is mightier": "level_end" } },
            level_end: { description: "You have passed the trial. The librarian gestures towards the central chamber. Type 'proceed' to enter.", image: "player", nextLevel: true, options: { "proceed": "" } }
        }
    },
    {
        title: "Level 21: The Second Word Shrine",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "The second shrine stands in the center of the chamber. As you approach, the stone and water around it coalesce into a towering form: an Ancient Guardian.", image: "guardian", options: { "awaken": "guardian_fight" } },
            guardian_fight: { description: "The Guardian raises a stone fist! It is the shrine's protector. Type 'history' and 'future' to dismantle its form!", image: "guardian", monster: { name: "Ancient Guardian", hp: 70 }, options: { "history": "guardian_hit", "future": "guardian_hit" } },
            guardian_hit: { description: "Your words resonate, shaking the Guardian's stone body!", image: "guardian", monster: { name: "Ancient Guardian", hp: 70 }, options: { "history": "guardian_hit", "future": "guardian_hit" } },
            guardian_victory: { description: "The Guardian crumbles. The shrine glows with power, and the forest outside begins to heal.", image: "player", options: { "vision": "level_end" } },
            level_end: { description: "A new vision appears: a shrine atop a frozen mountain peak. Type 'journey' to continue.", image: "player", nextLevel: true, options: { "journey": "" } }
        }
    },
     // ACT 3: The Frozen Peaks & The Forges of Creation
    {
        title: "Level 22: The Frost-Bitten Path",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "You begin your ascent of the Frozen Peaks. The air is thin and cold. Words seem to freeze as you think them.", image: "player", options: { "climb": "level_end" } },
            level_end: { description: "The climb is treacherous. Type 'ascend' to continue upwards.", image: "player", nextLevel: true, options: { "ascend": "" } }
        }
    },
    {
        title: "Level 23: The Wolf's Howl",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "A chilling howl echoes through the peaks. A pack of Frost Wolves, their fur the color of snow, surrounds you.", image: "frost_wolf", options: { "stand": "wolf_fight" } },
            wolf_fight: { description: "The alpha wolf lunges! Type 'winter' and 'blizzard' to drive them back!", image: "frost_wolf", monster: { name: "Alpha Frost Wolf", hp: 75 }, options: { "winter": "wolf_hit", "blizzard": "wolf_hit" } },
            wolf_hit: { description: "You fight against the freezing winds and snapping jaws!", image: "frost_wolf", monster: { name: "Alpha Frost Wolf", hp: 75 }, options: { "winter": "wolf_hit", "blizzard": "wolf_hit" } },
            wolf_victory: { description: "The pack retreats into the snow. You find a hidden ice cave they were guarding.", image: "player", options: { "enter": "level_end" } },
            level_end: { description: "The cave offers shelter from the storm. Type 'rest' to take a moment.", image: "player", nextLevel: true, options: { "rest": "" } }
        }
    },
    {
        title: "Level 24: The Shifting Glaciers",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "You must cross a vast glacier. The ice groans and shifts. As you walk, the ice rises up, forming a living creature.", image: "ice_elemental", options: { "face": "ice_fight" } },
            ice_fight: { description: "An Ice Elemental blocks your path! Type 'shatter' and 'fracture' to break its frozen form!", image: "ice_elemental", monster: { name: "Ice Elemental", hp: 80 }, options: { "shatter": "ice_hit", "fracture": "ice_hit" } },
            ice_hit: { description: "You strike at the elemental's crystalline body!", image: "ice_elemental", monster: { name: "Ice Elemental", hp: 80 }, options: { "shatter": "ice_hit", "fracture": "ice_hit" } },
            ice_victory: { description: "The elemental explodes into a shower of ice. The path to the mountain's summit is now clear.", image: "player", options: { "summit": "level_end" } },
            level_end: { description: "You continue your climb to the peak. Type 'endure' to push through the cold.", image: "player", nextLevel: true, options: { "endure": "" } }
        }
    },
    {
        title: "Level 25: The Yeti's Lair",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "You find a large cave near the summit, the home of a legendary Yeti. It guards the pass to the other side of the mountain.", image: "yeti", options: { "challenge": "yeti_fight" } },
            yeti_fight: { description: "The Yeti roars, causing an avalanche! Type 'mountain' and 'avalanche' to withstand its might!", image: "yeti", monster: { name: "Great Yeti", hp: 85 }, options: { "mountain": "yeti_hit", "avalanche": "yeti_hit" } },
            yeti_hit: { description: "You battle the beast of the mountain!", image: "yeti", monster: { name: "Great Yeti", hp: 85 }, options: { "mountain": "yeti_hit", "avalanche": "yeti_hit" } },
            yeti_victory: { description: "The Yeti is defeated. It respects your strength and allows you to pass. It points to a plume of smoke in the distance.", image: "player", options: { "look": "level_end" } },
            level_end: { description: "The smoke comes from an ancient forge, built into the heart of the mountain. Type 'descend' to investigate.", image: "player", nextLevel: true, options: { "descend": "" } }
        }
    },
    {
        title: "Level 26: The Forges of Creation",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "You have found the Forges of Creation, where words can be given form. To face the dragon, you must forge a 'Keystone of Serenity'.", image: "player", options: { "forge": "level_end" } },
            level_end: { description: "The forge is cold. You must relight it. Type 'ignite' to begin.", image: "player", nextLevel: true, options: { "ignite": "" } }
        }
    },
    {
        title: "Level 27: Trial of Embers",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "To light the forge, you must type words of heat and flame.", image: "player", options: { "ember fire flame": "level_end" } },
            level_end: { description: "The forge roars to life! Now you need the raw materials. Type 'gather' to find them.", image: "player", nextLevel: true, options: { "gather": "" } }
        }
    },
    {
        title: "Level 28: Trial of Ores",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "You must gather words of strength and resilience to serve as the ore.", image: "player", options: { "stone iron steel": "level_end" } },
            level_end: { description: "You have the materials. Now you must face the forge's guardian. Type 'summon' to call it.", image: "player", nextLevel: true, options: { "summon": "" } }
        }
    },
    {
        title: "Level 29: The Third Word Shrine",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "The heat of the forge coalesces into a being of metal and fire: The Forge Master. It will test your worthiness.", image: "forge_master", options: { "prove": "forge_fight" } },
            forge_fight: { description: "The Forge Master swings a mighty hammer! Type 'create' and 'shape' to withstand the blows and forge the keystone!", image: "forge_master", monster: { name: "The Forge Master", hp: 90 }, options: { "create": "forge_hit", "shape": "forge_hit" } },
            forge_hit: { description: "Sparks fly as you type words of creation against the Master's might!", image: "forge_master", monster: { name: "The Forge Master", hp: 90 }, options: { "create": "forge_hit", "shape": "forge_hit" } },
            forge_victory: { description: "The Forge Master relents. The Keystone of Serenity is complete. The third shrine glows, its power surging through the mountains.", image: "player", options: { "take": "level_end" } },
            level_end: { description: "With the Keystone, you can now face the Dragon not with violence, but with understanding. Type 'final' to begin the last part of your journey.", image: "player", nextLevel: true, options: { "final": "" } }
        }
    },
    // ACT 4: The Dragon's Lair
    {
        title: "Level 30: The Dragon's Peak",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "You stand at the entrance to the Dragon's Lair. The ground trembles. You hold the Keystone; its calm warmth gives you courage.", image: "player", options: { "steel": "level_end" } },
            level_end: { description: "You are ready for the final confrontation. Type 'challenge' to face the dragon.", image: "player", nextLevel: true, options: { "challenge": "" } }
        }
    },
    {
        title: "Level 31: The Dragon's Rage",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "You enter a grand hall. The Dragon is there, wreathed in shadows of sorrow and rage. It is the source of the Silence.", image: "dragon", options: { "reveal": "dragon_fight" } },
            dragon_fight: { description: "The Dragon roars, a sound of pure despair! Use the Keystone! Type 'peace' and 'serenity' to quell its rage!", image: "dragon_awake", monster: { name: "Sorrowful Dragon", hp: 100 }, options: { "peace": "dragon_hit", "serenity": "dragon_hit" } },
            dragon_hit: { description: "Your words of calm push back the shadows around the Dragon!", image: "dragon_awake", monster: { name: "Sorrowful Dragon", hp: 100 }, options: { "peace": "dragon_hit", "serenity": "dragon_hit", "calm": "dragon_hit" } },
            dragon_victory: { description: "The Dragon's rage subsides. Its sorrowful eyes look at you with understanding. The Curse of Silence is broken!", image: "mentor", options: { "story": "story_end" } },
            story_end: { description: "You have saved the kingdom and restored language! Your adventure is complete.", image: "mentor", victory: true, options: {} }
        }
    },
    {
        title: "Level 32: The Spire Approach",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "Before the final ascent, you must cross the 'Chasm of Whispers'. A rope bridge is the only way.", image: "player", options: { "cross": "level_end"} },
            level_end: { description: "The bridge sways dangerously. Type 'balance' to make it across.", image: "player", nextLevel: true, options: { "balance": ""} }
        }
    },
    {
        title: "Level 33: A Minor Squabble",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "A few stray Goblins, emboldened by the mountain's energy, block your path. They look more scared than angry.", image: "goblin", options: { "shoo": "goblin_fight"} },
            goblin_fight: { description: "You draw your sword. Type 'go away' to scare them off.", image: "goblin", monster: { name: "Scared Goblin", hp: 10 }, options: { "go away": "goblin_victory"} },
            goblin_victory: { description: "The goblins yelp and run away, leaving the path clear.", image: "player", options: { "onward": "level_end"} },
            level_end: { description: "The path is clear once more. Type 'climb' to continue.", image: "player", nextLevel: true, options: { "climb": ""} }
        }
    },
    {
        title: "Level 34: The Elder's Echo",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "A ghostly image of your village Elder appears. 'Remember why you fight,' he says. 'For every story, every name, every word.'", image: "mentor", options: { "remember": "level_end"} },
            level_end: { description: "Your resolve is strengthened. Type 'vow' to reaffirm your quest.", image: "player", nextLevel: true, options: { "vow": ""} }
        }
    },
    {
        title: "Level 35: A Rocky Obstacle",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "A rockslide has blocked the path. You see a hulking Golem made of loose stones nearby. It seems dormant.", image: "golem", options: { "awaken": "golem_fight"} },
            golem_fight: { description: "The Rock Golem awakens! It's slow but powerful. Type 'pebble' and 'boulder' to break it apart!", image: "golem", monster: { name: "Rock Golem", hp: 70 }, options: { "pebble": "golem_hit", "boulder": "golem_hit"} },
            golem_hit: { description: "You chip away at the Golem's rocky form.", image: "golem", monster: { name: "Rock Golem", hp: 70 }, options: { "pebble": "golem_hit", "boulder": "golem_hit"} },
            golem_victory: { description: "The Golem crumbles into a pile of rocks, conveniently filling a gap in the path.", image: "player", options: { "cross": "level_end"} },
            level_end: { description: "You walk across the newly formed path. Type 'higher' to continue.", image: "player", nextLevel: true, options: { "higher": ""} }
        }
    },
    {
        title: "Level 36: A Moment of Doubt",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "The peak seems so far away. The air is so cold. For a moment, you wonder if you can do this. The memory of your fading village fills your mind.", image: "player", options: { "strength": "level_end"} },
            level_end: { description: "You find new strength in your purpose. Type 'persevere' to carry on.", image: "player", nextLevel: true, options: { "persevere": ""} }
        }
    },
    {
        title: "Level 37: The Final Gate",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "An ancient gate, covered in frost, blocks the way to the Dragon's lair. It is sealed by a word of power.", image: "shield", options: { "examine": "gate_riddle"} },
            gate_riddle: { description: "The inscription reads: 'I have no voice, but I tell all stories.'", image: "shield", options: { "a book": "level_end" } },
            level_end: { description: "The gate swings open. The final challenge is just beyond. Type 'enter' to face your destiny.", image: "player", nextLevel: true, options: { "enter": ""} }
        }
    },
    {
        title: "Level 38: The Antechamber",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "The heat is intense. You stand at the entrance to the Dragon's Lair. The ground trembles with each of its breaths. Your final challenge awaits.", image: "player", options: { "steel": "enter_lair" } },
            enter_lair: { description: "You take a deep breath and step into the lair. This is it. The fate of the kingdom rests on your fingers.", image: "player", options: { "face": "level_end" } },
            level_end: { description: "You are ready for the final battle. Type 'challenge' to face the dragon.", image: "player", nextLevel: true, options: { "challenge": "" } }
        }
    },
    {
        title: "Level 39: The Dragon's Sorrow",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "The Dragon does not look at you with malice, but with immense sadness. The Silence is not a curse of evil, but of forgotten sorrow.", image: "dragon", options: { "understand": "dragon_fight" } },
            dragon_fight: { description: "You must remind the Dragon of what has been forgotten! Type words of 'hope' and 'love' to begin healing its heart!", image: "dragon_awake", monster: { name: "Sorrowful Dragon", hp: 100 }, options: { "hope": "dragon_hit", "love": "dragon_hit" } },
            dragon_hit: { description: "Your words pierce the veil of sorrow!", image: "dragon_awake", monster: { name: "Sorrowful Dragon", hp: 100 }, options: { "hope": "dragon_hit", "love": "dragon_hit", "joy": "dragon_hit" } },
            dragon_victory: { description: "The Dragon's rage subsides. Its sorrowful eyes look at you with understanding.", image: "mentor", options: { "peace": "level_end" } },
            level_end: { description: "The final seal on the curse is about to break. Type 'remember' to deliver the final word.", image: "player", nextLevel: true, options: { "remember": "" } }
        }
    },
    {
        title: "Level 40: The Word of Unmaking",
        allowedKeys: ALL_GAME_KEYS,
        startScene: "start",
        scenes: {
            start: { description: "The Curse of Silence coalesces into one final form, a being of pure nothingness that guards the Dragon's heart.", image: "dragon_awake", options: { "confront": "final_boss_fight" } },
            final_boss_fight: { description: "This is the true enemy! The embodiment of all that is forgotten. Type the most powerful words you know to defeat it: 'language', 'story', 'memory'!", image: "dragon_awake", monster: { name: "The Silence", hp: 120 }, options: { "language": "final_boss_hit", "story": "final_boss_hit", "memory": "final_boss_hit" } },
            final_boss_hit: { description: "You strike at the heart of nothingness with words of pure meaning!", image: "dragon_awake", monster: { name: "The Silence", hp: 120 }, options: { "language": "final_boss_hit", "story": "final_boss_hit", "memory": "final_boss_hit" } },
            final_boss_victory: { description: "The Silence shatters! The Dragon is free, and light returns to the world. The curse is broken!", image: "sunstone", options: { "celebrate": "story_end" } },
            story_end: { description: "You have saved the kingdom and restored language! Your adventure is complete.", image: "mentor", victory: true, options: {} }
        }
    }
];

const TypingRpgGame: React.FC<TypingRpgGameProps> = ({ user }) => {
    const [log, setLog] = useState<Array<{ type: string; text: string }>>([]);
    const [storyData, setStoryData] = useState<Level[]>(initialStory);
    const [levelIndex, setLevelIndex] = useState(0);
    const [sceneId, setSceneId] = useState(storyData[0].startScene);
    const [playerInput, setPlayerInput] = useState("");
    const [gameState, setGameState] = useState<'playing' | 'over' | 'victory'>('playing');
    const [playerHp, setPlayerHp] = useState(20);
    const [monsterHp, setMonsterHp] = useState(0);
    const [currentImage, setCurrentImage] = useState<string | undefined>(undefined);
    const [isLoadingProgress, setIsLoadingProgress] = useState(true);
    const inputRef = useRef<HTMLInputElement>(null);
    const logEndRef = useRef<HTMLDivElement>(null);
    const isCombatLoopRef = useRef(false);

    const currentLevel = storyData[levelIndex];
    const currentScene = currentLevel?.scenes[sceneId];
    const allowedKeys = currentLevel?.allowedKeys;
    const playerMaxHp = 20 + (levelIndex * 5);

    const initializeGameAtLevel = (levelIdx: number) => {
        let finalLevelIdx = levelIdx;
        if (!storyData[finalLevelIdx]) {
            console.error(`Invalid level index ${finalLevelIdx}, resetting to 0.`);
            finalLevelIdx = 0;
        }
        
        const level = storyData[finalLevelIdx];
        const startScene = level.scenes[level.startScene];
        
        setLevelIndex(finalLevelIdx);
        setSceneId(level.startScene);
        setLog([{type: 'story', text: `--- ${level.title} ---`}, { type: 'story', text: startScene.description }]);
        setCurrentImage(startScene.image);
        setPlayerHp(20 + (finalLevelIdx * 5));
        setMonsterHp(startScene.monster?.hp || 0);
        setGameState('playing');
        setPlayerInput("");
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    useEffect(() => {
        const loadAndInitialize = async () => {
            setIsLoadingProgress(true);
            let startLevel = 0;
            
            // FIX: Only fetch from server if the user is logged in and not a guest
            if (user && user.user_id && user.user_id !== 'guest') {
                try {
                    const progress = await api.get<{ levelIndex: number }>('/api/v1/student/rpg-progress');
                    startLevel = progress.levelIndex || 0;
                } catch (e) {
                    if (!(e instanceof Error && e.message.includes('401'))) {
                        console.error("Could not load RPG progress, starting from scratch.", e);
                    }
                }
            }
            
            initializeGameAtLevel(startLevel);
            setIsLoadingProgress(false);
        };
        loadAndInitialize();
    }, [user]);

    useEffect(() => {
        if (isLoadingProgress || !currentScene) return;

        if (!isCombatLoopRef.current) {
            addLog('story', currentScene.description);
        }
        isCombatLoopRef.current = false;

        setCurrentImage(currentScene.image);
        if (currentScene.monster && monsterHp <= 0) {
            setMonsterHp(currentScene.monster.hp);
        }
        if (currentScene.victory) {
            playSuccessSound();
            setGameState('victory');
        } else if (Object.keys(currentScene.options).length === 0 && !currentScene.nextLevel) {
            playGameOverSound();
            setGameState('over');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sceneId, levelIndex, isLoadingProgress]);


    useEffect(() => {
        logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [log]);

    const addLog = (type: string, text: string) => {
        setLog(prev => [...prev.slice(-100), { type, text }]);
    };
    
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        const oldValue = playerInput;
        if (gameState !== 'playing') { setPlayerInput(newValue); return; }
        if (newValue.length > oldValue.length) {
            const typedChar = newValue.slice(-1);
            if (typedChar !== ' ' && !allowedKeys.includes(typedChar)) {
                playWrongSound();
            } else {
                playKeyPressSound();
            }
        }
        setPlayerInput(newValue);
    };

    const handleNextLevel = async () => {
        const nextLevelIndex = levelIndex + 1;
        if(nextLevelIndex < storyData.length) {
            // FIX: Only save if user is a real user
            if (user && user.user_id && user.user_id !== 'guest') {
                try {
                    if (nextLevelIndex < initialStory.length) {
                       await api.post('/api/v1/student/rpg-progress', { levelIndex: nextLevelIndex });
                    }
                } catch (e) {
                    console.error("Failed to save progress", e);
                }
            }
            playSuccessSound();
            initializeGameAtLevel(nextLevelIndex);
        }
    }

    const resetGame = () => {
        // FIX: Only reset server if user is a real user
        if (user && user.user_id && user.user_id !== 'guest') {
            api.post('/api/v1/student/rpg-progress', { levelIndex: 0 }).catch(e => console.error("Failed to reset progress on server", e));
        }
        setStoryData(initialStory);
        initializeGameAtLevel(0);
    }

    const handleCommand = (e: React.FormEvent) => {
        e.preventDefault();
        const command = playerInput.trim();
        if (!command || gameState !== 'playing' || !currentScene) return;
        
        addLog('player', `> ${command}`);
        setPlayerInput("");
    
        if (currentScene.nextLevel) {
            handleNextLevel();
            return;
        }
    
        const commandKey = command.toLowerCase();
        let validCommand = Object.keys(currentScene.options).find(opt => opt.toLowerCase() === commandKey);
    
        if (validCommand) {
            const commandChars = new Set(command.split(''));
            for (const char of commandChars) {
                if (char !== ' ' && !allowedKeys.includes(char)) {
                    playWrongSound();
                    addLog('error', `Invalid key '${char}'. You can only use: [${allowedKeys.replace(" ", "")}]`);
                    return;
                }
            }
            playCorrectSound();
            
            const nextSceneId = currentScene.options[validCommand];

            if (currentScene.monster && (nextSceneId.includes('hit') || /fight/.test(nextSceneId))) {
                const hitMessages = ["A solid hit!", "A direct strike!", "Your attack connects!", "A clean blow!"];
                const monsterAttackMessages = ["It strikes back furiously!", "The creature retaliates!", "It attacks with rage!", "It lunges at you!"];
                
                const damage = Math.floor(Math.random() * (5 + levelIndex * 2)) + (3 + levelIndex);
                const newMonsterHp = monsterHp - damage;
                
                addLog('damage', `${hitMessages[Math.floor(Math.random() * hitMessages.length)]} You dealt ${damage} damage to the ${currentScene.monster.name}!`);
                setMonsterHp(newMonsterHp);
    
                if (newMonsterHp <= 0) {
                    const victorySceneId = sceneId.includes('fight') ? sceneId.replace(/_fight/g, '_victory') : sceneId.replace(/_hit/g, '_victory');
                    const finalVictorySceneId = currentLevel.scenes[victorySceneId] ? victorySceneId : 'victory';
                    isCombatLoopRef.current = false; // Allow victory message to show
                    setSceneId(finalVictorySceneId);
                    return;
                }
                
                isCombatLoopRef.current = true;
    
                const monsterDamage = Math.floor(Math.random() * (4 + Math.floor(levelIndex/2))) + (1 + Math.floor(levelIndex/2));
                const newPlayerHp = playerHp - monsterDamage;
                addLog('damage-enemy', `${monsterAttackMessages[Math.floor(Math.random() * monsterAttackMessages.length)]} The ${currentScene.monster.name} attacks you for ${monsterDamage} damage!`);
                setPlayerHp(newPlayerHp);
                
                if (newPlayerHp <= 0) {
                    addLog('error', 'You have been defeated.');
                    playGameOverSound();
                    setGameState('over');
                    return;
                }
                
                setSceneId(nextSceneId);
    
            } else {
                 if (nextSceneId !== sceneId) { // Prevent "You proceed..." on self-referential scenes
                    addLog('info', `You proceed...`);
                 }
                 setSceneId(nextSceneId);
            }
        } else {
            playWrongSound();
            addLog('error', `Unknown command: "${command}"`);
        }
    };
    
    if (isLoadingProgress) {
        return <div className="w-full h-96 flex items-center justify-center bg-gray-900 text-white font-mono p-4 rounded-lg">Loading your adventure...</div>
    }

    if (!currentScene) {
         return <div className="w-full h-96 flex items-center justify-center bg-gray-900 text-red-500 font-mono p-4 rounded-lg">Error: Game state is invalid. Please restart. <button onClick={resetGame} className="ml-4 px-4 py-2 bg-blue-600 text-white rounded font-sans font-bold">Restart</button></div>
    }

    const availableCommands = Object.keys(currentScene.options);
    
    return (
        <div className="w-full flex flex-col md:flex-row gap-4 bg-gray-900 text-white font-mono p-4 rounded-lg shadow-inner" onClick={() => inputRef.current?.focus()}>
            <div className="flex-shrink-0 md:w-1/3 text-center">
                <h3 className="text-lg text-yellow-300 mb-2">{currentLevel.title}</h3>
                {currentImage ? <RpgSprite name={currentImage} /> : <div className="w-32 h-32 mx-auto my-4 bg-black"></div>}
                <div className="p-3 bg-gray-800 rounded space-y-2">
                    <div>
                        <div className="flex justify-between text-xs text-green-400 mb-0.5">
                            <span>Player</span><span>{playerHp} / {playerMaxHp}</span>
                        </div>
                        <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full bg-green-500 rounded-full transition-all duration-300" style={{ width: `${Math.max(0, (playerHp / playerMaxHp) * 100)}%` }} />
                        </div>
                    </div>
                    {monsterHp > 0 && currentScene.monster && (
                        <div>
                            <div className="flex justify-between text-xs text-red-400 mb-0.5">
                                <span>{currentScene.monster.name}</span><span>{monsterHp} / {currentScene.monster.hp}</span>
                            </div>
                            <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                                <div className="h-full bg-red-500 rounded-full transition-all duration-300" style={{ width: `${Math.max(0, (monsterHp / currentScene.monster.hp) * 100)}%` }} />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex-grow flex flex-col">
                <div className="h-80 overflow-y-auto mb-4 pr-2 border-b-2 border-gray-700 pb-2">
                    {log.map((entry, i) => (
                        <p key={i} className={`mb-1 ${
                            entry.type === 'story' ? 'text-cyan-400' :
                            entry.type === 'player' ? 'text-white' :
                            entry.type === 'error' ? 'text-red-500' :
                            entry.type === 'damage' ? 'text-green-500' :
                            entry.type === 'damage-enemy' ? 'text-yellow-500' :
                            entry.type === 'info' ? 'text-gray-400' : ''
                        }`}>{entry.text}</p>
                    ))}
                    <div ref={logEndRef} />
                </div>
                
                {gameState === 'playing' ? (
                     <form onSubmit={handleCommand}>
                        <div className="mb-2 text-yellow-400">
                           <p>Available commands: [ {availableCommands.join(', ')} ]</p>
                           <p className="text-xs text-gray-500">Allowed keys: [ All letter keys ]</p>
                        </div>
                        <div className="flex items-center">
                            <span className="text-green-400 mr-2">&gt;</span>
                            <input
                                ref={inputRef}
                                type="text"
                                value={playerInput}
                                onChange={handleInputChange}
                                className="bg-transparent border-none text-white w-full focus:outline-none text-lg"
                                autoFocus
                            />
                        </div>
                    </form>
                ) : (
                    <div className="text-center">
                         {gameState === 'victory' && <Confetti />}
                         <p className="text-yellow-400 text-xl mb-4">
                            --- { gameState === 'victory' ? "VICTORY!" : "GAME OVER" } ---
                        </p>
                        <button onClick={resetGame} className="px-4 py-2 bg-blue-600 text-white rounded font-sans font-bold">Play Again</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TypingRpgGame;